/**
 * CookieBanner - GDPR-compliant consent banner + preferences modal.
 *
 * Behaviour
 *   - Fetches banner copy + default policy from /api/seo/consent.
 *   - Reads the trex_consent cookie. If missing / stale (banner version bumped),
 *     shows the banner in the corner.
 *   - Three actions: Accept all, Reject non-essential, Preferences.
 *   - Preferences opens a modal with per-category toggles. Necessary is always
 *     on (locked).
 *   - On save, writes the cookie AND fires a global event + Google Consent
 *     Mode v2 update via the GTM `gtag('consent','update',...)` command.
 *
 * Styling matches the T-REX Network night surfaces (aubergine + champagne).
 */
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import {
  readConsent,
  writeConsent,
  hasValidConsent,
  toConsentModeV2,
  DEFAULT_DENY,
} from "../lib/consent";
import { Cookie, X, Shield, BarChart3, Megaphone, Loader2 } from "lucide-react";

function applyToGTM(consent) {
  // Push a consent update whether or not GTM has loaded yet — if it loads
  // later, the dataLayer command replays.
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  gtag("consent", "update", toConsentModeV2(consent));
  window.dataLayer.push({
    event: "trex_consent_update",
    consent_analytics: consent.analytics ? "granted" : "denied",
    consent_marketing: consent.marketing ? "granted" : "denied",
  });
}

// Expose helpers so a "Manage cookies" link in the footer can re-open the
// banner even after a choice is made, and so admins can preview the corner
// banner from the SEO console.
if (typeof window !== "undefined") {
  window.__trex_openCookieSettings = () => {
    window.dispatchEvent(new CustomEvent("trex:openCookieSettings"));
  };
  window.__trex_previewCookieBanner = () => {
    window.dispatchEvent(new CustomEvent("trex:previewCookieBanner"));
  };
}

export default function CookieBanner() {
  const [copy, setCopy] = useState(null);
  const [open, setOpen] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [choice, setChoice] = useState(DEFAULT_DENY);

  // Load config + decide whether to show
  useEffect(() => {
    let alive = true;
    api.get("/seo/consent").then((r) => {
      if (!alive) return;
      const cfg = r.data;
      setCopy(cfg);
      if (!cfg.banner_enabled) return;
      const existing = readConsent();
      if (!hasValidConsent(existing, cfg.version)) {
        setOpen(true);
      } else {
        setChoice({
          necessary: true,
          analytics: !!existing.analytics,
          marketing: !!existing.marketing,
        });
      }
    }).catch(() => { /* silent: banner just won't show */ });
    return () => { alive = false; };
  }, []);

  // Footer "Manage cookies" link re-opens preferences
  useEffect(() => {
    const openPrefs = () => {
      const cur = readConsent();
      if (cur) {
        setChoice({ necessary: true, analytics: !!cur.analytics, marketing: !!cur.marketing });
      }
      setPrefsOpen(true);
      setOpen(true);
    };
    const previewBanner = () => {
      // Show the corner banner (not the prefs modal) so admins can see the
      // real first-visit UI without clearing their cookie.
      setPrefsOpen(false);
      setOpen(true);
    };
    window.addEventListener("trex:openCookieSettings", openPrefs);
    window.addEventListener("trex:previewCookieBanner", previewBanner);
    return () => {
      window.removeEventListener("trex:openCookieSettings", openPrefs);
      window.removeEventListener("trex:previewCookieBanner", previewBanner);
    };
  }, []);

  if (!copy || !copy.banner_enabled || !open) return null;

  const acceptAll = () => save({ necessary: true, analytics: true, marketing: true });
  const rejectAll = () => save({ necessary: true, analytics: false, marketing: false });
  const savePrefs = () => save(choice);

  function save(next) {
    const stored = writeConsent(next, copy.version);
    applyToGTM(stored);
    setPrefsOpen(false);
    setOpen(false);
  }

  const positionCls = {
    "bottom": "left-1/2 -translate-x-1/2 bottom-4 sm:bottom-6",
    "bottom-left": "left-4 sm:left-6 bottom-4 sm:bottom-6",
    "bottom-right": "right-4 sm:right-6 bottom-4 sm:bottom-6",
  }[copy.banner_position] || "right-4 sm:right-6 bottom-4 sm:bottom-6";

  return (
    <>
      {/* Banner */}
      {!prefsOpen && (
        <div
          role="dialog"
          aria-labelledby="trex-cookie-title"
          aria-describedby="trex-cookie-body"
          data-testid="cookie-banner"
          className={`fixed z-[70] ${positionCls} w-[calc(100vw-2rem)] sm:w-[440px] max-w-full`}
          style={{
            "--tx": copy.banner_position === "bottom" ? "-50%" : "0",
            animation: "trex-slide-in 320ms cubic-bezier(.2,.65,.35,1) both",
          }}
        >
          <div className="rounded-xl border border-champagne/25 bg-aubergine-raised text-bone shadow-2xl overflow-hidden">
            <div className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <Cookie className="h-4 w-4 text-champagne" />
                <div id="trex-cookie-title" className="font-serif text-base leading-none">{copy.title}</div>
              </div>
              <p id="trex-cookie-body" className="text-[13px] leading-snug text-bone/75">
                {copy.body}
                {" "}
                {copy.policy_url && (
                  <a href={copy.policy_url} className="text-champagne hover:underline whitespace-nowrap">
                    {copy.policy_label}
                  </a>
                )}
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 p-3 pt-0">
              <button
                onClick={acceptAll}
                data-testid="cookie-banner-accept"
                className="rounded-md px-3 py-2 font-mono text-[11px] uppercase tracking-[0.12em] bg-champagne text-aubergine hover:bg-champ-light transition-colors"
              >
                {copy.accept_label}
              </button>
              <button
                onClick={rejectAll}
                data-testid="cookie-banner-reject"
                className="rounded-md px-3 py-2 font-mono text-[11px] uppercase tracking-[0.12em] border border-bone/20 text-bone hover:bg-bone/5 transition-colors"
              >
                {copy.reject_label}
              </button>
              <button
                onClick={() => setPrefsOpen(true)}
                data-testid="cookie-banner-preferences"
                className="rounded-md px-3 py-2 font-mono text-[11px] uppercase tracking-[0.12em] text-bone/65 hover:text-bone hover:bg-bone/5 transition-colors"
              >
                {copy.preferences_label}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preferences modal */}
      {prefsOpen && (
        <div className="fixed inset-0 z-[75] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-aubergine/70 backdrop-blur-sm" data-testid="cookie-preferences">
          <div className="w-full max-w-lg rounded-t-2xl sm:rounded-xl border border-champagne/25 bg-aubergine-raised text-bone shadow-2xl overflow-hidden flex flex-col max-h-[92vh]">
            <header className="flex items-center justify-between px-5 py-3 border-b border-bone/10">
              <div className="flex items-center gap-2">
                <Cookie className="h-4 w-4 text-champagne" />
                <div className="font-serif text-lg">{copy.preferences_label}</div>
              </div>
              <button
                onClick={() => setPrefsOpen(false)}
                aria-label="Close preferences"
                className="p-1 rounded text-bone/45 hover:text-bone hover:bg-bone/5"
                data-testid="cookie-preferences-close"
              >
                <X className="h-4 w-4" />
              </button>
            </header>
            <div className="p-5 space-y-4 overflow-y-auto flex-1">
              <p className="text-[13px] leading-snug text-bone/70">{copy.body}</p>
              <CategoryToggle
                testid="cookie-prefs-necessary"
                icon={<Shield className="h-4 w-4" />}
                label={copy.necessary_label}
                description={copy.necessary_description}
                checked={true}
                locked
                onChange={() => {}}
              />
              <CategoryToggle
                testid="cookie-prefs-analytics"
                icon={<BarChart3 className="h-4 w-4" />}
                label={copy.analytics_label}
                description={copy.analytics_description}
                checked={!!choice.analytics}
                onChange={(v) => setChoice((p) => ({ ...p, analytics: v }))}
              />
              <CategoryToggle
                testid="cookie-prefs-marketing"
                icon={<Megaphone className="h-4 w-4" />}
                label={copy.marketing_label}
                description={copy.marketing_description}
                checked={!!choice.marketing}
                onChange={(v) => setChoice((p) => ({ ...p, marketing: v }))}
              />
              {copy.policy_url && (
                <a href={copy.policy_url} className="inline-block text-[11px] font-mono uppercase tracking-[0.15em] text-champagne hover:underline">
                  Read the {copy.policy_label.toLowerCase()}
                </a>
              )}
            </div>
            <footer className="flex items-center justify-end gap-3 px-5 py-3 border-t border-bone/10">
              <button
                onClick={rejectAll}
                data-testid="cookie-prefs-reject"
                className="text-[11px] font-mono uppercase tracking-[0.12em] text-bone/55 hover:text-bone"
              >
                {copy.reject_label}
              </button>
              <button
                onClick={savePrefs}
                data-testid="cookie-prefs-save"
                className="rounded-md px-4 py-2 font-mono text-[11px] uppercase tracking-[0.12em] bg-champagne text-aubergine hover:bg-champ-light"
              >
                {copy.save_preferences_label}
              </button>
            </footer>
          </div>
        </div>
      )}

      <style>{`
        @keyframes trex-slide-in {
          from { opacity: 0; transform: translate(var(--tx, -50%), 16px); }
          to   { opacity: 1; transform: translate(var(--tx, -50%), 0); }
        }
      `}</style>
    </>
  );
}

function CategoryToggle({ testid, icon, label, description, checked, onChange, locked }) {
  return (
    <div className="rounded-lg border border-bone/10 p-4 flex items-start justify-between gap-4" data-testid={testid}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-bone">
          <span className="text-champagne">{icon}</span>
          <span className="font-medium">{label}</span>
          {locked && (
            <span className="font-mono text-[9px] uppercase tracking-wider text-bone/45 border border-bone/15 rounded px-1.5 py-0.5">Always on</span>
          )}
        </div>
        <p className="mt-1.5 text-[12px] text-bone/60 leading-snug">{description}</p>
      </div>
      <label className="relative inline-flex items-center cursor-pointer flex-shrink-0 mt-1">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => !locked && onChange(e.target.checked)}
          disabled={locked}
          className="peer sr-only"
          data-testid={`${testid}-input`}
        />
        <span
          className={`w-9 h-5 rounded-full transition-colors ${
            checked ? "bg-champagne" : "bg-bone/15"
          } ${locked ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
        />
        <span
          className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
            checked ? "translate-x-4" : "translate-x-0"
          }`}
        />
      </label>
    </div>
  );
}
